import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getLead from "../http/getLead";
import getLeads from "../http/getLeads";
import deleteLeadSearch, {
  DeleteLeadSearchRequest,
} from "../http/deleteLeadSearch";
import getLeadSearch from "../http/getLeadSearch";
import getLeadSearches from "../http/getLeadSearches";
import patchLeadSearch, {
  RenameLeadSearchRequest,
} from "../http/patchLeadSearch";
import { getVoaRatingListStatus } from "../http/getVoaStatus";
import patchLead, { UpdateLeadRequest } from "../http/patchLead";
import patchLeadDecisions, {
  UpdateLeadDecisionsRequest,
} from "../http/patchLeadDecisions";
import patchVoaReview, { VoaReviewRequest } from "../http/patchVoaReview";
import getOperations, {
  deleteOperation as deleteOperationHttp,
  getOperationByKey,
  postCancelOperation,
} from "../http/getOperations";
import postLeadPremiumEnrich from "../http/postLeadPremiumEnrich";
import postLeadReenrichAddress, {
  LeadReenrichAddressRequest,
} from "../http/postLeadReenrichAddress";
import postLeadRefetch from "../http/postLeadRefetch";
import postLeadsBulkPremiumEnrich, {
  LeadsBulkPremiumEnrichRequest,
} from "../http/postLeadsBulkPremiumEnrich";
import postVoaRefetch from "../http/postVoaRefetch";
import postVoaRefetchDegraded from "../http/postVoaRefetchDegraded";
import postLeadCommercialResolve from "../http/postLeadCommercialResolve";
import postVoaReject from "../http/postVoaReject";
import postVoaViewed from "../http/postVoaViewed";
import postVoaPromote, { VoaPromoteRequest } from "../http/postVoaPromote";
import postVoaPremiumEnrich, {
  VoaPremiumEnrichRequest,
} from "../http/postVoaPremiumEnrich";
import postVoaResearchLandlord from "../http/postVoaResearchLandlord";
import postVoaResearchLandlordBulk, {
  postLeadsResearchLandlordBulk,
  postResearchCancelRow,
} from "../http/postVoaResearchLandlordBulk";
import postLeadResearchLandlord from "../http/postLeadResearchLandlord";
import getCompanyIdentityTrace from "../http/getCompanyIdentityTrace";
import getLandlordResearchTrace from "../http/getLandlordResearchTrace";
import getLandlordResearchPrompt from "../http/getLandlordResearchPrompt";
import getLandlordResearchPromptDiff from "../http/getLandlordResearchPromptDiff";
import {
  deleteLandlordPromptRule,
  reorderLandlordPromptRules,
  restoreLandlordPromptRevision,
  saveLandlordPromptAddendum,
  saveLandlordPromptPreamble,
  saveLandlordPromptRule,
} from "../http/landlordResearchPromptOps";
import postLandlordResearchPromptTest, {
  LandlordPromptTestRequest,
} from "../http/postLandlordResearchPromptTest";
import postLeadBulkTags, { BulkTagsRequest } from "../http/postLeadBulkTags";
import postLeadBulkDelete, {
  BulkDeleteRequest,
} from "../http/postLeadBulkDelete";
import {
  postBulkCreateSites,
  postBulkCreateSitesPreview,
} from "../http/postBulkCreateSites";
import { deleteLead } from "../http/deleteLead";
import { CsvImportRequest, postLeadImportCsv } from "../http/postLeadImport";
import postLeadSearch, { LeadSearchRequest } from "../http/postLeadSearch";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const useLeads = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const fetchLeads = useQuery(
    ["leads"],
    async () => getLeads(await getAuthTokenBlocking(getToken, relogin)),
    { enabled: true }
  );

  const searchLeadCandidates = useMutation(async (body: LeadSearchRequest) =>
    postLeadSearch(await getAuthTokenBlocking(getToken, relogin), body)
  );

  // A read-only "peek" at the current search results. Used to poll for
  // progressively-enriched rows while the server-side worker runs its
  // premium (Step 5) pass, which sets premiumEnrichedAt per row. Kept
  // separate from searchLeadCandidates so its loading state doesn't
  // disable the Search button on every poll. /leads/search is a pure
  // read once the base passes are done (needs_enrichment excludes
  // premium), so a peek never re-triggers enrichment or a duplicate op.
  const peekLeadCandidates = useMutation(async (body: LeadSearchRequest) =>
    postLeadSearch(await getAuthTokenBlocking(getToken, relogin), body)
  );

  const updateLead = useMutation(
    async ({ id, ...body }: UpdateLeadRequest & { id: string }) =>
      patchLead(await getAuthTokenBlocking(getToken, relogin), id, body),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  const updateLeadDecisions = useMutation(
    async ({ id, ...body }: UpdateLeadDecisionsRequest & { id: string }) =>
      patchLeadDecisions(
        await getAuthTokenBlocking(getToken, relogin),
        id,
        body
      ),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  const reviewVoaCandidate = useMutation(
    async ({ sourceRef, ...body }: VoaReviewRequest & { sourceRef: string }) =>
      patchVoaReview(
        await getAuthTokenBlocking(getToken, relogin),
        sourceRef,
        body
      )
  );

  const rejectVoaCandidate = useMutation(async (sourceRef: string) =>
    postVoaReject(await getAuthTokenBlocking(getToken, relogin), sourceRef)
  );

  const markVoaCandidateViewed = useMutation(async (sourceRef: string) =>
    postVoaViewed(await getAuthTokenBlocking(getToken, relogin), sourceRef)
  );

  const refetchVoaCandidate = useMutation(async (sourceRef: string) =>
    postVoaRefetch(await getAuthTokenBlocking(getToken, relogin), sourceRef)
  );

  // Bulk variant: re-runs only the upstream sources that were
  // degraded last time the row was enriched (driven by each row's
  // persisted `enrichmentDegradedSources`). Cheaper than
  // refetchVoaCandidate when only one upstream had a transient
  // outage - we don't bust the buildings/HMLR/EPC caches.
  const refetchVoaDegradedSources = useMutation(async (sourceRefs: string[]) =>
    postVoaRefetchDegraded(
      await getAuthTokenBlocking(getToken, relogin),
      sourceRefs
    )
  );

  // Kicks off the Companies House / Google Maps lookups for rows still showing
  // "Calculating". The list endpoints only ever READ that view from cache, so
  // without this a pending row would never settle.
  const resolveLeadCommercial = useMutation(
    async (body: {
      leadIds?: string[];
      sourceRefs?: string[];
      siteIds?: string[];
    }) =>
      postLeadCommercialResolve(
        await getAuthTokenBlocking(getToken, relogin),
        body
      )
  );

  const premiumEnrichVoaCandidates = useMutation(
    async (body: VoaPremiumEnrichRequest) =>
      postVoaPremiumEnrich(await getAuthTokenBlocking(getToken, relogin), body)
  );

  const refetchLead = useMutation(
    async (leadId: string) =>
      postLeadRefetch(await getAuthTokenBlocking(getToken, relogin), leadId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  const premiumEnrichLead = useMutation(
    async (leadId: string) =>
      postLeadPremiumEnrich(
        await getAuthTokenBlocking(getToken, relogin),
        leadId
      ),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  // Kick off the agentic landlord-research LRO for one VOA row. Returns
  // {operationKey, siteId} immediately; the FE polls useOperation(...)
  // until terminal, then invalidates the leads cache so the researched
  // candidates show. ~$0.20/run, so it's an explicit operator action.
  const researchVoaLandlord = useMutation(async (sourceRef: string) =>
    postVoaResearchLandlord(
      await getAuthTokenBlocking(getToken, relogin),
      sourceRef
    )
  );

  // Promoted-lead sibling of researchVoaLandlord.
  const researchLeadLandlord = useMutation(async (leadId: string) =>
    postLeadResearchLandlord(
      await getAuthTokenBlocking(getToken, relogin),
      leadId
    )
  );

  // Kick off bulk landlord research over the selected /leads/search rows as one
  // LRO. Returns {operationKey, rowCount, ...} immediately (or status:"noop" when
  // every selected row was already researched); the FE polls useOperation(...)
  // then re-runs the search. Paid (~$2/row), so it's gated behind a cost confirm.
  const bulkResearchVoaLandlords = useMutation(async (sourceRefs: string[]) =>
    postVoaResearchLandlordBulk(
      await getAuthTokenBlocking(getToken, relogin),
      sourceRefs
    )
  );

  // Sibling of bulkResearchVoaLandlords for the /leads inbox: researches the
  // selected promoted leads by lead id. Same LRO + poll + refresh flow.
  const bulkResearchLeadLandlords = useMutation(async (leadIds: string[]) =>
    postLeadsResearchLandlordBulk(
      await getAuthTokenBlocking(getToken, relogin),
      leadIds
    )
  );

  // Pull one still-queued row out of an in-flight bulk batch (the per-row Cancel
  // on the panel while the row shows "Queued"). Removes just that row; the rest
  // of the queue keeps running. Shared by both inbox surfaces.
  const cancelResearchRow = useMutation(
    async ({
      operationKey,
      sourceRef,
    }: {
      operationKey: string;
      sourceRef: string;
    }) =>
      postResearchCancelRow(
        await getAuthTokenBlocking(getToken, relogin),
        operationKey,
        sourceRef
      )
  );

  // The editable landlord-research prompt is a rule set (a preamble plus
  // addressable rules). Each write appends one revision; every mutation
  // invalidates the prompt query so the card re-renders (and its baseRevision
  // advances) from the fresh active revision. All are gated on `baseRevision`
  // optimistic concurrency, so a stale edit 409s rather than clobbering another.
  const invalidatePrompt = {
    onSuccess: () => {
      queryClient.invalidateQueries(["landlordResearchPrompt"]);
    },
  };
  const saveLandlordPromptRuleMut = useMutation(
    async (body: Parameters<typeof saveLandlordPromptRule>[1]) =>
      saveLandlordPromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePrompt
  );
  const deleteLandlordPromptRuleMut = useMutation(
    async (body: Parameters<typeof deleteLandlordPromptRule>[1]) =>
      deleteLandlordPromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePrompt
  );
  const reorderLandlordPromptRulesMut = useMutation(
    async (body: Parameters<typeof reorderLandlordPromptRules>[1]) =>
      reorderLandlordPromptRules(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePrompt
  );
  const saveLandlordPromptPreambleMut = useMutation(
    async (body: Parameters<typeof saveLandlordPromptPreamble>[1]) =>
      saveLandlordPromptPreamble(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePrompt
  );
  const saveLandlordPromptAddendumMut = useMutation(
    async (body: Parameters<typeof saveLandlordPromptAddendum>[1]) =>
      saveLandlordPromptAddendum(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePrompt
  );
  const restoreLandlordPromptRevisionMut = useMutation(
    async (body: Parameters<typeof restoreLandlordPromptRevision>[1]) =>
      restoreLandlordPromptRevision(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    invalidatePrompt
  );

  // Kick off a test research run against a DRAFT prompt (not saved) as an LRO.
  // Returns {operationKey, siteId}; the caller polls useOperation(...) and reads
  // candidates + trace ref from the operation result. Persists nothing.
  const testLandlordResearchPrompt = useMutation(
    async (body: LandlordPromptTestRequest) =>
      postLandlordResearchPromptTest(
        await getAuthTokenBlocking(getToken, relogin),
        body
      )
  );

  // Persist an operator-corrected address on a lead and re-run
  // enrichment from it. Unlike refetchLead (VOA-only), this works for
  // CSV-imported leads too.
  const reenrichLeadAddress = useMutation(
    async (req: LeadReenrichAddressRequest) =>
      postLeadReenrichAddress(
        await getAuthTokenBlocking(getToken, relogin),
        req
      ),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  // Kick off the bulk premium-enrich as a long-running operation.
  // Returns {operationKey, siteId, leadCount} immediately; the actual
  // enrichment runs in the long-running-operation Lambda. The FE polls
  // `useOperation(operationKey, siteId)` until status is terminal,
  // then invalidates the leads cache so the refreshed rows show up.
  const bulkPremiumEnrichLeads = useMutation(
    async (body: LeadsBulkPremiumEnrichRequest) =>
      postLeadsBulkPremiumEnrich(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    {
      onSuccess: () => {
        // Mutation only kicks off the LRO; the real invalidation
        // happens when the polling hook sees the terminal "completed"
        // status. Harmless insurance against any other surface
        // showing a partly-stale leads list.
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const promoteVoaCandidates = useMutation(
    async (body: VoaPromoteRequest) =>
      postVoaPromote(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        // The search-history list's Promoted column is derived from the leads
        // this just created, so it is stale the moment a promote lands - it read
        // 0 beside rows the same page was already badging as Leads until a hard
        // reload. Rename and delete already invalidate it for the same reason.
        queryClient.invalidateQueries(["leadSearches"]);
      },
    }
  );

  const bulkUpdateLeadTags = useMutation(
    async (body: BulkTagsRequest) =>
      postLeadBulkTags(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const bulkDeleteLeads = useMutation(
    async (body: BulkDeleteRequest) =>
      postLeadBulkDelete(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        // Same reason as promote: deleting leads moves the search-history
        // list's Promoted count, which is derived from them.
        queryClient.invalidateQueries(["leadSearches"]);
      },
    }
  );

  // Plan: returns the preview shape (sitesToCreate, skipped,
  // orgsToReuse, orgsToCreate). The FE renders this in a confirm
  // modal; no writes yet.
  const previewBulkCreateSites = useMutation(async (leadIds: string[]) =>
    postBulkCreateSitesPreview(
      await getAuthTokenBlocking(getToken, relogin),
      leadIds
    )
  );

  // Kick off the bulk-create as a long-running operation. Returns
  // {operationKey, siteId} immediately; the actual create work
  // happens in the long-running-operation Lambda. The FE polls
  // `useOperation(operationKey, siteId)` until status === "completed"
  // and reads the `result` for the created sites/orgs/skipped lists.
  const bulkCreateSites = useMutation(
    async (leadIds: string[]) =>
      postBulkCreateSites(
        await getAuthTokenBlocking(getToken, relogin),
        leadIds
      ),
    {
      onSuccess: () => {
        // Created sites land in the Sites list + new orgs land in
        // the Organisations list; invalidate both so the admin app's
        // other tabs reflect the bulk action.
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["sites"]);
        queryClient.invalidateQueries(["organisations"]);
      },
    }
  );

  // Kick off the CSV/XLSX import as a long-running operation. Returns
  // {operationKey, siteId, rowCount} immediately; the per-row
  // enrichment + persistence happens in the long-running-operation
  // Lambda. The FE polls `useOperation(operationKey, siteId)` until
  // status === "completed" and reads the `result` for the created
  // leads + per-row errors. Mirrors `bulkCreateSites`.
  const importLeadsFromCsv = useMutation(
    async (body: CsvImportRequest) =>
      postLeadImportCsv(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        // The operation only just kicked off; the real invalidation
        // happens when the polling hook sees the terminal "completed"
        // status. This invalidate is harmless insurance.
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const cancelOperation = useMutation(async (operationKey: string) =>
    postCancelOperation(
      await getAuthTokenBlocking(getToken, relogin),
      operationKey
    )
  );

  const deleteOperation = useMutation(async (operationKey: string) =>
    deleteOperationHttp(
      await getAuthTokenBlocking(getToken, relogin),
      operationKey
    )
  );

  const deleteLeadMutation = useMutation(
    async (leadId: string) =>
      deleteLead(await getAuthTokenBlocking(getToken, relogin), leadId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return {
    fetchLeads,
    searchLeadCandidates,
    peekLeadCandidates,
    updateLead,
    updateLeadDecisions,
    reviewVoaCandidate,
    rejectVoaCandidate,
    markVoaCandidateViewed,
    refetchVoaCandidate,
    refetchVoaDegradedSources,
    resolveLeadCommercial,
    premiumEnrichVoaCandidates,
    researchVoaLandlord,
    researchLeadLandlord,
    bulkResearchVoaLandlords,
    bulkResearchLeadLandlords,
    cancelResearchRow,
    saveLandlordPromptRuleMut,
    deleteLandlordPromptRuleMut,
    reorderLandlordPromptRulesMut,
    saveLandlordPromptPreambleMut,
    saveLandlordPromptAddendumMut,
    restoreLandlordPromptRevisionMut,
    testLandlordResearchPrompt,
    refetchLead,
    premiumEnrichLead,
    reenrichLeadAddress,
    bulkPremiumEnrichLeads,
    promoteVoaCandidates,
    bulkUpdateLeadTags,
    bulkDeleteLeads,
    previewBulkCreateSites,
    bulkCreateSites,
    importLeadsFromCsv,
    deleteLead: deleteLeadMutation,
    cancelOperation,
    deleteOperation,
  };
};

// The Search history list. Invalidated by a rename and by every recorded run,
// so the list reflects the latest run without the page having to re-fetch by hand.
export const useLeadSearches = (enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadSearches"],
    async () => getLeadSearches(await getAuthTokenBlocking(getToken, relogin)),
    { enabled }
  );
};

export const useLeadSearch = (searchId: string | null) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadSearch", searchId],
    async () =>
      getLeadSearch(
        await getAuthTokenBlocking(getToken, relogin),
        searchId as string
      ),
    { enabled: searchId != null }
  );
};

export const useRenameLeadSearch = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useMutation(
    async (body: RenameLeadSearchRequest) =>
      patchLeadSearch(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leadSearches"]);
        // The rename moved the search's tag on every lead promoted from it, so
        // the inbox's tag column and tag filter are both stale until refetched.
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );
};

// Deleting a search invalidates the leads too: with `removeTags` the leads it
// promoted just lost a tag, which the inbox's tag column and tag filter render.
export const useDeleteLeadSearch = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useMutation(
    async (body: DeleteLeadSearchRequest) =>
      deleteLeadSearch(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leadSearches"]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );
};

export const useLeadSearchOperations = (opts?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadSearchOperations"],
    async () => getOperations(await getAuthTokenBlocking(getToken, relogin)),
    {
      enabled: opts?.enabled ?? true,
      // Default 5s polling matches the design doc's status-page
      // cadence; callers can override (false to disable, or a
      // longer interval when nothing is in flight).
      refetchInterval: opts?.refetchInterval ?? 5000,
    }
  );
};

// Single-operation polling hook. Used by surfaces that have just
// kicked off a long-running operation and want to watch *that one*
// row finish (rather than re-fetching the whole list every tick).
// Stops polling once the operation reaches a terminal status. Pass
// `null` for either id to disable.
export const useOperation = (
  operationKey: string | null,
  siteId: string | null,
  opts?: { refetchInterval?: number | false }
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["operation", operationKey, siteId],
    async () =>
      getOperationByKey(
        await getAuthTokenBlocking(getToken, relogin),
        operationKey!,
        siteId!
      ),
    {
      enabled: !!operationKey && !!siteId,
      refetchInterval: (data) => {
        if (
          data?.operation?.status &&
          ["completed", "failed", "cancelled"].includes(data.operation.status)
        ) {
          return false;
        }
        return opts?.refetchInterval ?? 2000;
      },
    }
  );
};

// Opt-in landlord-research AI trace fetch (only when the operator asks -
// it can be large). A runId fetches that specific run; a null runId fetches
// the subject's most recent trace, so a row researched in another session is
// still viewable. Pass enabled=false to hold off until the "View AI trace"
// toggle is on.
export const useLandlordResearchTrace = (
  subjectRef: string | null,
  runId: string | null,
  enabled: boolean
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["landlordResearchTrace", subjectRef, runId],
    async () =>
      getLandlordResearchTrace(
        await getAuthTokenBlocking(getToken, relogin),
        subjectRef!,
        runId!
      ),
    // runId is read off the persisted row and is required by the endpoint - only
    // fire once it exists (no "latest for this subject" fallback).
    { enabled: !!subjectRef && !!runId && enabled }
  );
};

// The AI trace for the call that worked out what company a tenant label names -
// the step the trading name, the Google Maps site count and (through the Companies
// House name search) the size band all descend from. Keyed on the COMPANY, not the
// lead: the call is made once per company and every row naming that tenant reads
// the same answer, so they all open the same trace. Opt-in like the research trace,
// since a trace can be large.
export const useCompanyIdentityTrace = (
  companyName: string | null,
  runId: string | null,
  enabled: boolean
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["companyIdentityTrace", companyName, runId],
    async () =>
      getCompanyIdentityTrace(
        await getAuthTokenBlocking(getToken, relogin),
        companyName!,
        runId!
      ),
    { enabled: !!companyName && !!runId && enabled }
  );
};

// The editable landlord-research prompt (Stage C): active revision + full
// history + the non-editable enforced footer. Admin-only (inrange-org gated).
export const useLandlordResearchPrompt = (enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["landlordResearchPrompt"],
    async () =>
      getLandlordResearchPrompt(await getAuthTokenBlocking(getToken, relogin)),
    { enabled }
  );
};

// Rule-level + text diff between two prompt revisions (Stage C / #4). Admin-only.
export const useLandlordResearchPromptDiff = (
  fromRevision: number | null,
  toRevision: number | null,
  enabled = true
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["landlordResearchPromptDiff", fromRevision, toRevision],
    async () =>
      getLandlordResearchPromptDiff(
        await getAuthTokenBlocking(getToken, relogin),
        fromRevision!,
        toRevision!
      ),
    { enabled: enabled && fromRevision !== null && toRevision !== null }
  );
};

export const useLead = (leadId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(
    ["lead", leadId],
    async () => getLead(await getAuthTokenBlocking(getToken, relogin), leadId!),
    { enabled: !!leadId }
  );
};

export const useVoaRatingListStatus = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(["voaStatus"], async () =>
    getVoaRatingListStatus(await getAuthTokenBlocking(getToken, relogin))
  );
};

export default useLeads;
